import { FC } from 'react';
import { beautifyThePrice, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { SalesEstimation, SmartDecisionValue } from './types';
import styles from './index.module.less';

interface CalculateFunnelProps {
  salesEstimation?: SalesEstimation;
}

interface CalculateStep {
  label: string;
  labelHint: string;
  value: string;
  connector?: string;
  className?: string;
}

/**
 * @description 销售额测算过程
 */
const CalculateFunnel: FC<CalculateFunnelProps> = ({ salesEstimation }) => {
  const methods = useMethods({
    formatNumber(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return beautifyThePrice(value || '', ',', 'auto');
    },
    formatPercent(value: SmartDecisionValue) {
      if (!isNotEmpty(value)) return '-';
      return `${value}%`;
    },
    formatMoney(value: SmartDecisionValue, suffix: string) {
      if (!isNotEmpty(value)) return '-';
      return `${methods.formatNumber(value)}${suffix}`;
    },
    getSteps(data?: SalesEstimation): CalculateStep[] {
      const enterRate = methods.formatPercent(data?.adjustedEnterRate);
      const conversionRate = methods.formatPercent(data?.conversionRate);
      const rate1 = methods.formatPercent(data?.enterRate);
      const rate2 = data?.floorCoefficient;
      const rate3 = data?.brandCoefficient;

      let targetHintText = `相似店均值${rate1} `;
      if (rate2 && rate2 !== '-') {
        targetHintText += `x 楼层系数 ${rate2}`;
      }
      if (rate3 && rate3 !== '-') {
        targetHintText += `× 品牌系数 ${rate3}`;
      }
      const unitPrice = methods.formatMoney(data?.unitPrice, '元');
      return [
        {
          label: '点位日均客流',
          labelHint: '基准客流数据',
          value: methods.formatMoney(data?.dailyFlow, '人/日'),
          connector: `× 进店率 ${enterRate}（${targetHintText}）`,
        },
        {
          label: '预估进店客流',
          labelHint: '客流 × 进店率',
          value: methods.formatMoney(data?.estimatedEnterCount, '人/日'),
          connector: `× 转化率 ${conversionRate}（相似店均值）`,
        },
        {
          label: '预估购买',
          labelHint: '进店 × 转化率',
          value: methods.formatMoney(data?.estimatedPurchaseCount, '人/日'),
          connector: `× 客单价 ¥${unitPrice === '-' ? '-' : methods.formatNumber(data?.unitPrice)}`,
        },
        {
          label: '日销售额',
          labelHint: '购买人数 × 客单价',
          value: methods.formatMoney(data?.dailySales, '元/日'),
          connector: '× 30 天',
          className: 'calculationFlowStrong',
        },
        {
          label: '月预估销售额',
          labelHint: '日销售额 × 30 天',
          value: methods.formatMoney(data?.monthlySales, '元/月'),
          className: 'calculationFlowResult',
        },
      ];
    },
    renderStep(item: CalculateStep) {
      return (
        <div
          className={styles.calculationFlowItem}
          key={item.label}
        >
          <div
            className={item.className ? `${styles.calculationFlowStep} ${styles[item.className]}` : styles.calculationFlowStep}
          >
            <span className={styles.calculationFlowLabel}>
              <strong>{item.label}</strong>
              <small>{item.labelHint}</small>
            </span>
            <strong>{item.value}</strong>
          </div>
          {item.connector ? <div className={styles.connector}>{item.connector}</div> : null}
        </div>
      );
    },
  });

  return (
    <div className={styles.detailCard}>
      <div className={styles.chartTitle}>销售额测算过程</div>
      <div className={styles.calculationFlow}>
        {methods.getSteps(salesEstimation).map(methods.renderStep)}
      </div>
    </div>
  );
};

export default CalculateFunnel;
